import React, { createContext, type FC, type PropsWithChildren, useContext, useSyncExternalStore } from "react";

import { eventStreamClient, type EventStreamClient } from "./eventStreamClient";

const EventStreamContext = createContext<EventStreamClient | null>(null);

/**
 * Wraps authenticated app UI so hooks can access the shared event stream through context.
 * The underlying client is still a module singleton for legacy class components and tests.
 */
export const EventStreamProvider: FC<PropsWithChildren> = ({ children }) => (
	<EventStreamContext.Provider value={eventStreamClient}>{children}</EventStreamContext.Provider>
);

/**
 * Returns the shared SSE client. Prefer this over default import in new React code.
 * Works outside `EventStreamProvider` by falling back to the module singleton.
 */
export function useEventStream(): EventStreamClient {
	const fromContext = useContext(EventStreamContext);
	return fromContext ?? eventStreamClient;
}

/**
 * Subscribes to the current `EventSource` instance so effects can re-run when the connection is recreated.
 */
export function useEventSource(): EventSource | null {
	const client = useEventStream();
	return useSyncExternalStore(
		client.subscribeToEventSource,
		() => client.eventSource,
		() => client.eventSource
	);
}

export type { EventStreamClient };

export default eventStreamClient;
